/**
 * Connection Status Card Component
 * Displays Tally connection status with deployment functionality
 * Following PRD Phase 2 specifications (lines 243-248)
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useConnectionStore } from '../../stores/connectionStore';
import { CompanyFetchCoordinator } from '../../services/companyFetchCoordinator';
import { mcpHostService, MCPConnectionStatus } from '../../services/mcp-host';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import {
  Loader2,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Monitor,
  Calendar,
  Building,
  Settings,
} from 'lucide-react';

const ConnectionStatusCard: React.FC = () => {
  const {
    status,
    lastConnected,
    deviceName,
    tallyVersion,
    connectionId,
    companies,
    selectedCompany,
    isLoading,
    error,
    checkConnection,
    refreshWithCompanies,
    clearError
  } = useConnectionStore();

  const { toast } = useToast();
  const [isDeploying, setIsDeploying] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [mcpStatus, setMcpStatus] = useState<MCPConnectionStatus | null>(null);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // REMOVED: Auto-fetch companies effect to prevent duplicate API calls
  // Company fetching is now handled centrally by CompanyFetchCoordinator
  // and triggered only during dashboard initialization or manual refresh

  // Check MCP status for new users (handle 400 errors gracefully)
  useEffect(() => {
    const checkMcpStatus = async () => {
      try {
        const status = await mcpHostService.getMCPConnectionStatus();
        setMcpStatus(status);
      } catch (error) {
        // Handle 400 errors gracefully - likely means new user with no tools
        console.log('MCP status check (expected for new users):', error);
        setMcpStatus({
          isRunning: false,
          connectedTools: [], // Empty tools array for new users  
          overallStatus: 'disconnected',
          lastUpdated: new Date(),
          error: null // Don't treat as error for new users
        });
      }
    };

    // Check once on mount - reduced polling to be less aggressive
    checkMcpStatus();
  }, []);

  const getStatusConfig = () => {
    switch (status) {
      case 'connected':
        return {
          icon: CheckCircle,
          label: 'Connected',
          description: 'AccountsGenie is connected to Tally',
          variant: 'default' as const,
          bgColor: 'bg-green-50',
          textColor: 'text-green-700',
          borderColor: 'border-green-200'
        };
      case 'connecting':
        return {
          icon: Loader2,
          label: 'Deployed',
          description: 'Deployed - Awaiting Tally Connection',
          variant: 'secondary' as const,
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-200'
        };
      default:
        return {
          icon: AlertCircle,
          label: 'Disconnected',
          description: 'Ready to deploy - Install AccountsGenie Connector',
          variant: 'destructive' as const,
          bgColor: 'bg-orange-50',
          textColor: 'text-orange-700',
          borderColor: 'border-orange-200'
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  // Debounced refresh function to prevent excessive API calls
  const handleRefreshConnection = useCallback(async () => {
    if (isRefreshing) {
      return; // Prevent multiple simultaneous refreshes
    }
    
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    setIsRefreshing(true);
    
    try {
      // Step 1: Check connection status first
      await checkConnection(true);
      
      // Step 2: If connected, fetch companies
      const state = useConnectionStore.getState();
      if (state.status === 'connected') {
        // Use coordinated refresh to prevent race conditions and duplicate calls
        await CompanyFetchCoordinator.ensureCompaniesFetched(true, 'manual-refresh');
        
        toast({
          title: "Connection refreshed",
          description: "Connection status and companies list updated",
        });
      } else {
        toast({
          title: "Connection status updated",
          description: state.status === 'connecting' 
            ? "TallyConnector is attempting to connect" 
            : "TallyConnector is not connected. Please check if it's running.",
          variant: state.status === 'not_connected' ? "destructive" : "default",
        });
      }
    } catch (error) {
      toast({
        title: "Refresh failed",
        description: "Unable to refresh connection status",
        variant: "destructive",
      });
    } finally {
      // Add debounce delay before allowing next refresh
      refreshTimeoutRef.current = setTimeout(() => {
        setIsRefreshing(false);
      }, 2000); // 2 second debounce
    }
  }, [isRefreshing, checkConnection, refreshWithCompanies, toast]);
  
  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  const handleDeployConnector = async () => {
    try {
      setIsDeploying(true);
      
      // Get tenant_id first
      const tenantResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/user/tenant_id`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (!tenantResponse.ok) {
        throw new Error('Failed to get tenant ID');
      }
      
      const tenantData = await tenantResponse.json();
      const tenantId = tenantData.data;
      
      // Generate the EXE
      const generateResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/tcg/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ tenant_id: tenantId })
      });
      
      if (!generateResponse.ok) {
        const errorData = await generateResponse.json();
        throw new Error(errorData.message || 'Failed to generate installer');
      }
      
      const generateData = await generateResponse.json();
      
      if (!generateData.success) {
        throw new Error(generateData.message || 'Failed to generate installer');
      }
      
      // Download the actual file with correct URL and filename
      const downloadUrl = `${import.meta.env.VITE_API_URL}${generateData.data.download_url}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = generateData.data.filename; // Use actual filename from server
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "TallyConnector Downloaded",
        description: `Installer downloaded successfully. Please run it on your Windows PC with Tally.`,
      });
      
      // Update connection status to show "Connector Downloaded"
      // This will be updated to "Connected" once TC registers itself
      await checkConnection();
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Unable to generate connector download",
        variant: "destructive",
      });
    } finally {
      setIsDeploying(false);
    }
  };


  const formatLastConnected = (date: Date | string | number | null) => {
    if (!date) return 'Never';
    
    // Robust date handling - handle both Date objects and date strings/numbers
    let dateObj: Date;
    
    try {
      if (date instanceof Date) {
        // Already a Date object
        dateObj = date;
      } else if (typeof date === 'string') {
        // Convert string to Date
        dateObj = new Date(date);
        
        // Check if the date is valid
        if (isNaN(dateObj.getTime())) {
          return 'Invalid date';
        }
      } else if (typeof date === 'number') {
        // Handle timestamp
        dateObj = new Date(date);
        
        if (isNaN(dateObj.getTime())) {
          return 'Invalid timestamp';
        }
      } else {
        // Unknown type
        return 'Invalid date format';
      }
    } catch (error) {
      // Handle any parsing errors
      return 'Date parsing error';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    
    // Format date in IST (Indian Standard Time)
    return dateObj.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Card className={cn(
      "transition-all duration-200 hover:shadow-md",
      statusConfig.borderColor
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <StatusIcon className={cn(
              "h-5 w-5",
              status === 'connecting' && "animate-spin",
              statusConfig.textColor
            )} />
            <CardTitle className="text-lg">Tally Connection</CardTitle>
          </div>
          <div className="flex items-center space-x-2">
            <Badge variant={statusConfig.variant}>
              {statusConfig.label}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefreshConnection}
              disabled={isLoading || isRefreshing}
              className="h-6 w-6 p-0"
            >
              {(isLoading || isRefreshing) ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>
        <CardDescription>
          {statusConfig.description}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Error Display */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-red-800 font-medium">Connection Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearError}
                className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
              >
                ×
              </Button>
            </div>
          </div>
        )}

        {/* Connection Details */}
        {status === 'connected' && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3">
              {deviceName && (
                <div className="flex items-center space-x-2">
                  <Monitor className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Device:</span>
                  <span className="text-sm font-medium">{deviceName}</span>
                </div>
              )}
              
              
              {tallyVersion && (
                <div className="flex items-center space-x-2">
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Version:</span>
                  <span className="text-sm font-medium">{tallyVersion}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Companies Dropdown - Show when connected */}
        {status === 'connected' && (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Building className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground font-medium">Available Companies:</span>
              {isLoading && (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              )}
            </div>
            
            {companies && companies.length > 0 ? (
              <>
                <Select value={selectedCompany || ''} onValueChange={(value) => {
                  // Update selected company in store
                  useConnectionStore.setState({ selectedCompany: value });
                }}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a company..." />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.name} value={company.name}>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{company.name}</span>
                          </div>
                          {company.alias && company.alias !== company.name && (
                            <span className="text-xs text-muted-foreground">{company.alias}</span>
                          )}
                          {(company.is_locked || company.password_protected) && (
                            <div className="flex items-center space-x-2 mt-1">
                              {company.is_locked && (
                                <Badge variant="secondary" className="h-4 text-xs px-1">Locked</Badge>
                              )}
                              {company.password_protected && (
                                <Badge variant="outline" className="h-4 text-xs px-1">Protected</Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Found {companies.length} compan{companies.length === 1 ? 'y' : 'ies'} in Tally
                </p>
              </>
            ) : !isLoading ? (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground italic">
                  No companies found.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    try {
                      const result = await CompanyFetchCoordinator.ensureCompaniesFetched(true, 'manual-company-refresh');
                      if (result.success) {
                        toast({
                          title: "Companies refreshed",
                          description: `Found ${result.companiesCount || 0} companies`,
                        });
                      } else {
                        toast({
                          title: "Refresh skipped",
                          description: result.reason,
                          variant: "default",
                        });
                      }
                    } catch (error) {
                      toast({
                        title: "Refresh failed",
                        description: "Unable to load companies",
                        variant: "destructive",
                      });
                    }
                  }}
                  disabled={isLoading}
                  className="text-xs px-2 py-1 h-6"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Loading companies...
              </p>
            )}
          </div>
        )}

        {/* Last Connected */}
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Last connected:</span>
          <span className="text-sm font-medium">
            {formatLastConnected(lastConnected)}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-2 pt-2">
          {status === 'not_connected' && (
            <Button
              onClick={handleDeployConnector}
              disabled={isDeploying || isLoading}
              className="w-full"
              size="sm"
            >
              {isDeploying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Preparing Download...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Deploy AccountsGenie
                </>
              )}
            </Button>
          )}

        </div>

        {/* Connection Status Summary */}
        <div className="pt-2 border-t">
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="flex items-center space-x-1">
              <span className="text-muted-foreground">Tally:</span>
              <span className={cn(
                "font-medium",
                status === 'connected' ? 'text-green-600' : 
                status === 'connecting' ? 'text-blue-600' : 'text-red-600'
              )}>
                {status === 'connected' ? 'Connected' : 
                 status === 'connecting' ? 'Deployed' : 'Not Connected'}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="text-muted-foreground">Tools:</span>
              <span className={cn(
                "font-medium",
                mcpStatus?.overallStatus === 'connected' ? 'text-green-600' :
                mcpStatus?.overallStatus === 'partial' ? 'text-yellow-600' : 'text-red-600'
              )}>
                {mcpStatus?.overallStatus === 'connected' ? 'Connected' :
                 mcpStatus?.overallStatus === 'partial' ? 'Partial' :
                 mcpStatus?.overallStatus === 'error' ? 'Error' : 'Disconnected'}
              </span>
            </div>
          </div>
          {connectionId && status === 'connected' && (
            <p className="text-xs text-muted-foreground mt-2">
              Connection ID: {connectionId}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ConnectionStatusCard;